import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, AuthContext } from "../App";
import { toast } from "sonner";
import { ArrowLeft, User, Mail, Phone, Home, Save, Lock } from "lucide-react";

const ProfilePage = () => {
  const { user, login } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    name: "",
    email: "",
    phone: "",
    mailing_address: ""
  });
  const [originalProfile, setOriginalProfile] = useState({});

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    fetchProfile();
  }, [user, navigate]);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/members/profile`);
      setProfile({
        name: res.data.name || "",
        email: res.data.email || "",
        phone: res.data.phone || "",
        mailing_address: res.data.mailing_address || ""
      });
      setOriginalProfile(res.data);
    } catch (error) {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    // Only send changed fields
    const updates = {};
    if (profile.name !== originalProfile.name) updates.name = profile.name;
    if (profile.email !== originalProfile.email) updates.email = profile.email;
    if (profile.phone !== originalProfile.phone) updates.phone = profile.phone;
    if (profile.mailing_address !== originalProfile.mailing_address) updates.mailing_address = profile.mailing_address;

    if (Object.keys(updates).length === 0) {
      toast.info("No changes to save");
      setSaving(false);
      return;
    }

    try {
      const res = await axios.put(`${API}/members/profile`, updates);
      toast.success("Profile updated successfully!");
      
      // Update local user context if name or email changed
      if (updates.email || updates.name) {
        const updatedUser = { ...user };
        if (updates.name) updatedUser.name = updates.name;
        if (updates.email) updatedUser.email = updates.email;
        login(localStorage.getItem('token'), updatedUser);
      }
      
      setOriginalProfile(res.data.member);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = () => {
    return profile.name !== originalProfile.name ||
           profile.email !== originalProfile.email ||
           profile.phone !== originalProfile.phone ||
           profile.mailing_address !== originalProfile.mailing_address;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-amber-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="font-bold">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-amber-50 p-4 sm:p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 border-2 border-black bg-white hover:bg-gray-100"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl font-bold">KJ Profile</h1>
        </div>

        {/* Profile Form */}
        <div className="brutal-card">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b-2 border-gray-200">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center border-2 border-black">
              <User size={32} className="text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold">{originalProfile.name}</h2>
              <span className={`px-2 py-1 text-xs font-bold rounded ${
                originalProfile.is_master_admin 
                  ? 'bg-red-100 text-red-700' 
                  : originalProfile.is_admin 
                    ? 'bg-purple-100 text-purple-700'
                    : originalProfile.kj_allowance_eligible
                      ? 'bg-green-100 text-green-700'
                      : 'bg-yellow-100 text-yellow-700'
              }`}>
                {originalProfile.is_master_admin 
                  ? '👑 MASTER ADMIN' 
                  : originalProfile.is_admin 
                    ? '🔐 ADMIN'
                    : originalProfile.kj_allowance_eligible
                      ? '🎤 EXTERNAL KJ'
                      : '🏠 RN MEMBER'}
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div>
              <label className="flex items-center gap-2 text-sm font-bold mb-1">
                <User size={16} /> Full Name
              </label>
              <input
                type="text"
                className="brutal-input w-full notranslate"
                translate="no"
                value={profile.name}
                onChange={(e) => setProfile({...profile, name: e.target.value})}
                required
              />
            </div>

            {/* Email */}
            <div>
              <label className="flex items-center gap-2 text-sm font-bold mb-1">
                <Mail size={16} /> Email Address
              </label>
              <input
                type="email"
                className="brutal-input w-full notranslate"
                translate="no"
                value={profile.email}
                onChange={(e) => setProfile({...profile, email: e.target.value})}
                required
              />
              <p className="text-xs text-gray-500 mt-1">Used for login and notifications</p>
            </div>

            {/* Phone */}
            <div>
              <label className="flex items-center gap-2 text-sm font-bold mb-1">
                <Phone size={16} /> Phone Number
              </label>
              <input
                type="tel"
                className="brutal-input w-full notranslate"
                translate="no"
                value={profile.phone}
                onChange={(e) => setProfile({...profile, phone: e.target.value})}
                required
              />
            </div>

            {/* Address */}
            <div>
              <label className="flex items-center gap-2 text-sm font-bold mb-1">
                <Home size={16} /> Mailing Address
              </label>
              <textarea
                className="brutal-input w-full"
                rows={2}
                value={profile.mailing_address}
                onChange={(e) => setProfile({...profile, mailing_address: e.target.value})}
                required
              />
            </div>

            {/* KJ Role (Read-only) - Only show for non-admin members */}
            {!originalProfile.is_admin && !originalProfile.is_master_admin && (
              <div className="p-4 bg-gray-50 border-2 border-gray-300 rounded">
                <p className="text-sm font-bold mb-2">Your KJ Role</p>
                <div className={`p-3 rounded border-2 ${
                  originalProfile.kj_allowance_eligible 
                    ? 'bg-green-50 border-green-400' 
                    : 'bg-yellow-50 border-yellow-400'
                }`}>
                  <span className={`text-lg font-bold ${
                    originalProfile.kj_allowance_eligible ? 'text-green-700' : 'text-yellow-700'
                  }`}>
                    {originalProfile.kj_allowance_eligible ? '🎤 External KJ' : '🏠 RN Member'}
                  </span>
                  <div className="text-sm mt-1">
                    {originalProfile.kj_allowance_eligible 
                      ? '• $35 Allowance per completed session' 
                      : '• $0 Allowance (No payment)'}
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2 italic">
                  ⚠️ Role changes must be requested through Admin. Contact 6459 8286.
                </p>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={saving || !hasChanges()}
                className={`flex-1 brutal-btn-primary flex items-center justify-center gap-2 ${
                  !hasChanges() ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <Save size={18} />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>

          {/* Change Password Link */}
          <div className="mt-6 pt-4 border-t-2 border-gray-200">
            <button
              onClick={() => navigate('/change-password')}
              className="flex items-center gap-2 text-purple-600 hover:text-purple-800 font-bold"
            >
              <Lock size={18} />
              Change Password
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
